import React, { useState, useEffect } from 'react';
import { BarChart3, Calendar, TrendingUp, DollarSign, Car, Clock, Users } from 'lucide-react';
import { useAuth } from '../context/AuthContext';
import { vehiclesAPI, handleApiError } from '../services/api';
import LoadingSpinner from '../components/LoadingSpinner';
import { useFlashMessage } from '../components/FlashMessage';
import { FormSelect } from '../components/FormSelect';

const OwnerReports = () => {
  const { user } = useAuth();
  const { showSuccess, showDanger, FlashContainer } = useFlashMessage();
  const [reportType, setReportType] = useState('daily');
  const [dateRange, setDateRange] = useState({
    start: new Date(Date.now() - 7 * 24 * 60 * 60 * 1000).toISOString().split('T')[0], // 7 days ago
    end: new Date().toISOString().split('T')[0]
  });
  const [reportData, setReportData] = useState(null);
  const [isLoading, setIsLoading] = useState(false);

  useEffect(() => {
    loadReportData();
  }, [reportType, dateRange]);

  const loadReportData = async () => {
    if (!user?.locations || user.locations.length === 0) return;

    setIsLoading(true);
    try {
      let data = null;

      switch (reportType) {
        case 'daily':
          data = await generateDailyRevenueReport();
          break;
        case 'vehicle':
          data = await generateVehicleHistoryReport();
          break;
        case 'staff':
          data = await generateStaffPerformanceReport();
          break;
        default:
          data = await generateDailyRevenueReport();
      }

      setReportData(data);
    } catch (error) {
      console.error('Report loading error:', error);
      showDanger('Rapor yüklenirken hata oluştu: ' + error.message);
    } finally {
      setIsLoading(false);
    }
  };

  const generateDailyRevenueReport = async () => {
    const dailyData = [];
    let totalRevenue = 0;
    let totalVehicles = 0;

    // Her lokasyon için veri topla
    for (const location of user.locations) {
      try {
        // Aktif araçları al
        const activeResponse = await vehiclesAPI.getActiveVehicles(location.id);
        const activeVehicles = activeResponse.data.success ? activeResponse.data.data : [];

        // Teslim edilmiş araçları al
        const deliveredResponse = await vehiclesAPI.getDeliveredVehicles(location.id);
        const deliveredVehicles = deliveredResponse.data.success ? deliveredResponse.data.data : [];

        // Tarih aralığındaki araçları filtrele
        const startDate = new Date(dateRange.start);
        const endDate = new Date(dateRange.end + 'T23:59:59');

        const filteredVehicles = [
          ...activeVehicles.filter(v => {
            const checkInDate = new Date(v.check_in);
            return checkInDate >= startDate && checkInDate <= endDate;
          }),
          ...deliveredVehicles.filter(v => {
            const checkOutDate = v.check_out ? new Date(v.check_out) : null;
            return checkOutDate && checkOutDate >= startDate && checkOutDate <= endDate;
          })
        ];

        const locationRevenue = filteredVehicles.reduce((sum, v) => sum + (parseFloat(v.actual_amount) || 0), 0);
        totalRevenue += locationRevenue;
        totalVehicles += filteredVehicles.length;

        dailyData.push({
          location: location.name,
          revenue: locationRevenue,
          vehicleCount: filteredVehicles.length,
          vehicles: filteredVehicles
        });

      } catch (error) {
        console.warn(`Failed to load data for location ${location.id}:`, error);
      }
    }

    return {
      type: 'daily',
      summary: {
        totalRevenue,
        totalVehicles,
        averageRevenue: totalVehicles > 0 ? totalRevenue / totalVehicles : 0,
        period: `${dateRange.start} - ${dateRange.end}`
      },
      details: dailyData
    };
  };

  const generateVehicleHistoryReport = async () => {
    const vehicleHistory = [];

    for (const location of user.locations) {
      try {
        const activeResponse = await vehiclesAPI.getActiveVehicles(location.id);
        const deliveredResponse = await vehiclesAPI.getDeliveredVehicles(location.id);

        const allVehicles = [
          ...(activeResponse.data.success ? activeResponse.data.data : []),
          ...(deliveredResponse.data.success ? deliveredResponse.data.data : [])
        ];

        // Tarih aralığındaki araçları filtrele
        const startDate = new Date(dateRange.start);
        const endDate = new Date(dateRange.end + 'T23:59:59');

        const filteredVehicles = allVehicles.filter(v => {
          const vehicleDate = new Date(v.check_in);
          return vehicleDate >= startDate && vehicleDate <= endDate;
        });

        vehicleHistory.push(...filteredVehicles.map(v => ({
          ...v,
          locationName: location.name
        })));

      } catch (error) {
        console.warn(`Failed to load vehicles for location ${location.id}:`, error);
      }
    }

    return {
      type: 'vehicle',
      summary: {
        totalVehicles: vehicleHistory.length,
        period: `${dateRange.start} - ${dateRange.end}`
      },
      details: vehicleHistory.sort((a, b) => new Date(b.check_in) - new Date(a.check_in))
    };
  };

  const generateStaffPerformanceReport = async () => {
    const staffData = {};

    for (const location of user.locations) {
      try {
        const activeResponse = await vehiclesAPI.getActiveVehicles(location.id);
        const deliveredResponse = await vehiclesAPI.getDeliveredVehicles(location.id);

        const allVehicles = [
          ...(activeResponse.data.success ? activeResponse.data.data : []),
          ...(deliveredResponse.data.success ? deliveredResponse.data.data : [])
        ];

        // Tarih aralığındaki araçları filtrele
        const startDate = new Date(dateRange.start);
        const endDate = new Date(dateRange.end + 'T23:59:59');

        const filteredVehicles = allVehicles.filter(v => {
          const vehicleDate = new Date(v.check_in);
          return vehicleDate >= startDate && vehicleDate <= endDate;
        });

        // Staff performansını hesapla
        filteredVehicles.forEach(vehicle => {
          const staffName = vehicle.staff_name || 'Bilinmeyen';
          if (!staffData[staffName]) {
            staffData[staffName] = {
              name: staffName,
              vehicleCount: 0,
              totalRevenue: 0,
              locations: new Set()
            };
          }
          staffData[staffName].vehicleCount++;
          staffData[staffName].totalRevenue += parseFloat(vehicle.actual_amount) || 0;
          staffData[staffName].locations.add(location.name);
        });

      } catch (error) {
        console.warn(`Failed to load staff data for location ${location.id}:`, error);
      }
    }

    const staffArray = Object.values(staffData).map(staff => ({
      ...staff,
      locations: Array.from(staff.locations),
      averageRevenue: staff.vehicleCount > 0 ? staff.totalRevenue / staff.vehicleCount : 0
    })).sort((a, b) => b.totalRevenue - a.totalRevenue);

    return {
      type: 'staff',
      summary: {
        totalStaff: staffArray.length,
        period: `${dateRange.start} - ${dateRange.end}`
      },
      details: staffArray
    };
  };


  const formatCurrency = (amount) => {
    return new Intl.NumberFormat('tr-TR', {
      style: 'currency',
      currency: 'TRY'
    }).format(amount || 0);
  };

  const formatDate = (dateString) => {
    return new Date(dateString).toLocaleDateString('tr-TR');
  };

  const getStatusText = (status) => {
    const statusMap = {
      'received': 'Teslim Alındı',
      'washing': 'Yıkanıyor',
      'parked': 'Park Edildi',
      'ready': 'Hazır',
      'called': 'Çağrıldı',
      'delivered': 'Teslim Edildi'
    };
    return statusMap[status] || status;
  };

  const getStatusColor = (status) => {
    const colorMap = {
      'received': 'bg-blue-100 text-blue-800',
      'washing': 'bg-yellow-100 text-yellow-800',
      'parked': 'bg-purple-100 text-purple-800',
      'ready': 'bg-green-100 text-green-800',
      'called': 'bg-orange-100 text-orange-800',
      'delivered': 'bg-gray-100 text-gray-800'
    };
    return colorMap[status] || 'bg-gray-100 text-gray-800';
  };

  return (
    <div className="space-y-6">
      <FlashContainer />


      {/* Report controls */}
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-4">
        <FormSelect
          name="reportType"
          value={reportType}
          onChange={(e) => setReportType(e.target.value)}
          options={[
            { value: 'daily', label: 'Günlük Gelir Raporu' },
            { value: 'vehicle', label: 'Araç Geçmişi Raporu' },
            { value: 'staff', label: 'Personel Performans Raporu' }
          ]}
        />

        <input
          type="date"
          className="form-input"
          value={dateRange.start}
          onChange={(e) => setDateRange(prev => ({ ...prev, start: e.target.value }))}
        />

        <input
          type="date"
          className="form-input"
          value={dateRange.end}
          onChange={(e) => setDateRange(prev => ({ ...prev, end: e.target.value }))}
        />
      </div>

      {/* Loading */}
      {isLoading && (
        <div className="flex justify-center py-8">
          <LoadingSpinner />
        </div>
      )}

      {/* Report Content */}
      {!isLoading && reportData && (
        <div className="space-y-6">
          {/* Summary Cards */}
          {reportData.type === 'daily' && (
            <>
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
                <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6">
                  <div className="flex items-center">
                    <div className="p-2 bg-green-100 rounded-lg">
                      <DollarSign className="h-6 w-6 text-green-600" />
                    </div>
                    <div className="ml-4">
                      <p className="text-sm font-medium text-gray-600">Toplam Gelir</p>
                      <p className="text-2xl font-bold text-gray-900">
                        {formatCurrency(reportData.summary.totalRevenue)}
                      </p>
                    </div>
                  </div>
                </div>

                <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6">
                  <div className="flex items-center">
                    <div className="p-2 bg-blue-100 rounded-lg">
                      <Car className="h-6 w-6 text-blue-600" />
                    </div>
                    <div className="ml-4">
                      <p className="text-sm font-medium text-gray-600">Toplam Araç</p>
                      <p className="text-2xl font-bold text-gray-900">{reportData.summary.totalVehicles}</p>
                    </div>
                  </div>
                </div>

                <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6">
                  <div className="flex items-center">
                    <div className="p-2 bg-purple-100 rounded-lg">
                      <TrendingUp className="h-6 w-6 text-purple-600" />
                    </div>
                    <div className="ml-4">
                      <p className="text-sm font-medium text-gray-600">Ortalama Gelir</p>
                      <p className="text-2xl font-bold text-gray-900">
                        {formatCurrency(reportData.summary.averageRevenue)}
                      </p>
                    </div>
                  </div>
                </div>

                <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6">
                  <div className="flex items-center">
                    <div className="p-2 bg-orange-100 rounded-lg">
                      <Calendar className="h-6 w-6 text-orange-600" />
                    </div>
                    <div className="ml-4">
                      <p className="text-sm font-medium text-gray-600">Rapor Dönemi</p>
                      <p className="text-sm font-bold text-gray-900">{reportData.summary.period}</p>
                    </div>
                  </div>
                </div>
              </div>

              {/* Location Details */}
              <div className="bg-white rounded-lg shadow-sm border border-gray-200">
                <div className="px-6 py-4 border-b border-gray-200">
                  <h3 className="text-lg font-medium text-gray-900">Lokasyon Bazlı Detaylar</h3>
                </div>
                <div className="overflow-x-auto">
                  <table className="min-w-full divide-y divide-gray-200">
                    <thead className="bg-gray-50">
                      <tr>
                        <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                          Lokasyon
                        </th>
                        <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                          Araç Sayısı
                        </th>
                        <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                          Toplam Gelir
                        </th>
                        <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                          Ortalama
                        </th>
                      </tr>
                    </thead>
                    <tbody className="bg-white divide-y divide-gray-200">
                      {reportData.details.map((location, index) => (
                        <tr key={index}>
                          <td className="px-6 py-4 whitespace-nowrap text-sm font-medium text-gray-900">
                            {location.location}
                          </td>
                          <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                            {location.vehicleCount}
                          </td>
                          <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                            {formatCurrency(location.revenue)}
                          </td>
                          <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                            {formatCurrency(location.vehicleCount > 0 ? location.revenue / location.vehicleCount : 0)}
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </div>
            </>
          )}

          {/* Vehicle History Report */}
          {reportData.type === 'vehicle' && (
            <div className="bg-white rounded-lg shadow-sm border border-gray-200">
              <div className="px-6 py-4 border-b border-gray-200">
                <h3 className="text-lg font-medium text-gray-900">
                  Araç Geçmişi ({reportData.summary.totalVehicles} araç)
                </h3>
                <p className="text-sm text-gray-500">Dönem: {reportData.summary.period}</p>
              </div>
              <div className="overflow-x-auto">
                <table className="min-w-full divide-y divide-gray-200">
                  <thead className="bg-gray-50">
                    <tr>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                        Plaka
                      </th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                        Lokasyon
                      </th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                        Durum
                      </th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                        Giriş Tarihi
                      </th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                        Tutar
                      </th>
                    </tr>
                  </thead>
                  <tbody className="bg-white divide-y divide-gray-200">
                    {reportData.details.slice(0, 50).map((vehicle, index) => (
                      <tr key={index}>
                        <td className="px-6 py-4 whitespace-nowrap text-sm font-medium text-gray-900">
                          {vehicle.plate_number}
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                          {vehicle.locationName}
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap">
                          <span className={`inline-flex px-2 py-1 text-xs font-semibold rounded-full ${getStatusColor(vehicle.status)}`}>
                            {getStatusText(vehicle.status)}
                          </span>
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                          {formatDate(vehicle.check_in)}
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                          {formatCurrency(vehicle.actual_amount)}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
              {reportData.details.length > 50 && (
                <div className="px-6 py-4 bg-gray-50 text-center text-sm text-gray-500">
                  İlk 50 kayıt gösteriliyor. Tüm veriler için raporu indirin.
                </div>
              )}
            </div>
          )}

          {/* Staff Performance Report */}
          {reportData.type === 'staff' && (
            <div className="bg-white rounded-lg shadow-sm border border-gray-200">
              <div className="px-6 py-4 border-b border-gray-200">
                <h3 className="text-lg font-medium text-gray-900">
                  Personel Performans Raporu ({reportData.summary.totalStaff} personel)
                </h3>
                <p className="text-sm text-gray-500">Dönem: {reportData.summary.period}</p>
              </div>
              <div className="overflow-x-auto">
                <table className="min-w-full divide-y divide-gray-200">
                  <thead className="bg-gray-50">
                    <tr>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                        Personel
                      </th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                        Araç Sayısı
                      </th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                        Toplam Gelir
                      </th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                        Ortalama
                      </th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                        Lokasyonlar
                      </th>
                    </tr>
                  </thead>
                  <tbody className="bg-white divide-y divide-gray-200">
                    {reportData.details.map((staff, index) => (
                      <tr key={index}>
                        <td className="px-6 py-4 whitespace-nowrap text-sm font-medium text-gray-900">
                          {staff.name}
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                          {staff.vehicleCount}
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                          {formatCurrency(staff.totalRevenue)}
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                          {formatCurrency(staff.averageRevenue)}
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                          {staff.locations.join(', ')}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>
          )}
        </div>
      )}

      {/* No Data */}
      {!isLoading && (!reportData || (reportData.details && reportData.details.length === 0)) && (
        <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-8 text-center">
          <BarChart3 className="h-12 w-12 text-gray-400 mx-auto mb-4" />
          <h3 className="text-lg font-medium text-gray-900 mb-2">Veri Bulunamadı</h3>
          <p className="text-gray-500">Seçilen tarih aralığında rapor verisi bulunmuyor.</p>
        </div>
      )}
    </div>
  );
};

export default OwnerReports;